import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import type { RecommendationOptionsInput } from "../../../configurator";
import type { RootState } from "../../../redux/appState";
import {
  deleteMeasurement,
  setActiveTab,
  showToast,
  updateAssumptionsDraft,
  upsertMeasurement,
} from "../../../redux/coilConfigurator/actions";
import { buildMeasuredDataAggregateView, type MeasurementRecord } from "../../../web-app";
import CalibrationReviewModal from "./CalibrationReviewModal";
import MeasurementLogModal from "./MeasurementLogModal";
import MeasurementsList from "./MeasurementsList";
import "./measured-data-panel.css";

interface MeasuredDataPanelProps {
  records: MeasurementRecord[];
  assumptions: RecommendationOptionsInput | null;
  configurations: RootState["coilConfigurator"]["configurations"]["items"];
  catalogue: RootState["coilConfigurator"]["catalogue"]["active"];
  saveSubmitting: boolean;
  lastSaveSuccessAt: number;
  onUpsert: (record: MeasurementRecord, options?: { fromModal?: boolean }) => void;
  onDelete: (id: string) => void;
  onApplyCalibration: (assumptions: RecommendationOptionsInput) => void;
  onSwitchToAssumptions: () => void;
  onShowToast: (message: string) => void;
}

const MeasuredDataPanel = ({
  records,
  assumptions,
  configurations,
  catalogue,
  saveSubmitting,
  lastSaveSuccessAt,
  onUpsert,
  onDelete,
  onApplyCalibration,
  onSwitchToAssumptions,
  onShowToast,
}: MeasuredDataPanelProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editId, setEditId] = useState<string | null>(null);
  const lastHandledSaveSuccessAt = useRef(0);

  const aggregate = useMemo(() => buildMeasuredDataAggregateView(records), [records]);
  const inFitRecords = useMemo(() => records.filter((record) => record.inFit), [records]);
  const inFitCount = inFitRecords.length;

  const editRecord = useMemo(
    () => (editId ? records.find((record) => record.id === editId) ?? null : null),
    [editId, records],
  );

  const openCreateModal = () => {
    setModalMode("create");
    setEditId(null);
    setModalOpen(true);
  };

  const openEditModal = (id: string) => {
    setModalMode("edit");
    setEditId(id);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditId(null);
  };

  const handleSubmit = (record: MeasurementRecord) => {
    onUpsert(record, { fromModal: true });
  };

  useEffect(() => {
    if (!modalOpen) return;
    if (lastSaveSuccessAt > lastHandledSaveSuccessAt.current) {
      lastHandledSaveSuccessAt.current = lastSaveSuccessAt;
      closeModal();
    }
  }, [lastSaveSuccessAt, modalOpen]);

  const handleToggleInFit = (record: MeasurementRecord) => {
    onUpsert({ ...record, inFit: !record.inFit });
  };

  const handleDelete = (id: string) => {
    onDelete(id);
  };

  const handleApplyCalibration = (adjusted: RecommendationOptionsInput) => {
    onApplyCalibration(adjusted);
    setReviewModalOpen(false);
    onSwitchToAssumptions();
    window.scrollTo({ top: 0, behavior: "smooth" });
    onShowToast("Calibration applied. Review and save assumptions to persist.");
  };

  return (
    <section className="measured-data-workspace panel-card">
      <div className="measured-data-summary-card" aria-label="Measured data summary">
        <div className="stat-grid measured-data-stat-grid" id="measured-data-stat-grid">
          <div className="stat-card">
            <span className="stat-card__label">Records Logged</span>
            <span className="stat-card__value" id="measured-record-count">
              {aggregate.recordCount}
            </span>
          </div>
          <div className="stat-card">
            <span className="stat-card__label">Mean Cycle-Time Error</span>
            <span className="stat-card__value" id="measured-mean-cycle-error">
              {aggregate.meanCycleTimeErrorPercent}
            </span>
          </div>
          <div className="stat-card">
            <span className="stat-card__label">Mean Delivered-Power Error</span>
            <span className="stat-card__value" id="measured-mean-power-error">
              {aggregate.meanDeliveredPowerErrorPercent}
            </span>
          </div>
          <div className="stat-card">
            <span className="stat-card__label">Last Logged</span>
            <span className="stat-card__value" id="measured-last-logged">
              {aggregate.lastLoggedDate}
            </span>
          </div>
        </div>
      </div>

      <MeasurementsList
        records={records}
        onLogMeasurement={openCreateModal}
        onEditMeasurement={openEditModal}
        onDeleteMeasurement={handleDelete}
        onToggleInFit={handleToggleInFit}
        onReviewCalibration={() => setReviewModalOpen(true)}
        reviewCalibrationDisabled={inFitCount < 2 || assumptions == null}
      />

      <MeasurementLogModal
        open={modalOpen}
        mode={modalMode}
        editRecord={editRecord}
        configurations={configurations}
        catalogue={catalogue}
        submitting={saveSubmitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />

      {assumptions != null ? (
        <CalibrationReviewModal
          open={reviewModalOpen}
          inFitRecords={inFitRecords}
          assumptions={assumptions}
          onClose={() => setReviewModalOpen(false)}
          onApply={handleApplyCalibration}
        />
      ) : null}
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  records: state.coilConfigurator.measurements.records,
  assumptions: state.coilConfigurator.assumptions.active,
  configurations: state.coilConfigurator.configurations.items,
  catalogue: state.coilConfigurator.catalogue.active,
  saveSubmitting: state.coilConfigurator.measurements.saveSubmitting,
  lastSaveSuccessAt: state.coilConfigurator.measurements.lastSaveSuccessAt,
});

const mapDispatchToProps = {
  onUpsert: (record: MeasurementRecord, options?: { fromModal?: boolean }) =>
    upsertMeasurement(record, options),
  onDelete: deleteMeasurement,
  onApplyCalibration: updateAssumptionsDraft,
  onSwitchToAssumptions: () => setActiveTab("assumptions"),
  onShowToast: (message: string) => showToast(message),
};

export default connect(mapStateToProps, mapDispatchToProps)(MeasuredDataPanel);
